import { useCallback, useEffect, useRef, useState } from "react";
import { Room, DataPacket_Kind } from "livekit-client";
import { useChat } from "@livekit/components-react";
import { Send, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface ChatMessage {
  id: string;
  message: string;
  sender: string;
  timestamp: number;
  isLocal: boolean;
}

interface MeetingChatPanelProps {
  room: Room;
  currentUserName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function MeetingChatPanel({
  room,
  currentUserName,
  isOpen,
  onClose,
}: MeetingChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { chatMessages } = useChat();

  // Lắng nghe messages từ useChat hook
  useEffect(() => {
    if (chatMessages.length > 0) {
      const newMessages: ChatMessage[] = chatMessages.map((msg) => ({
        id: msg.id,
        message: msg.message,
        sender: msg.from?.name || "Unknown",
        timestamp: msg.timestamp,
        isLocal: msg.from?.identity === room.localParticipant.identity,
      }));
      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const uniqueNew = newMessages.filter((m) => !existingIds.has(m.id));
        return [...prev, ...uniqueNew].sort((a, b) => a.timestamp - b.timestamp);
      });
    }
  }, [chatMessages, room.localParticipant.identity]);

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!message.trim() || isSending) return;

      try {
        setIsSending(true);
        const payload = JSON.stringify({
          text: message.trim(),
          sender: currentUserName,
        });

        await room.localParticipant.publishData(
          new TextEncoder().encode(payload),
          {
            reliable: true,
          }
        );

        // Thêm message vào local state ngay lập tức
        const newMessage: ChatMessage = {
          id: `local-${Date.now()}`,
          message: message.trim(),
          sender: currentUserName,
          timestamp: Date.now(),
          isLocal: true,
        };
        setMessages((prev) => [...prev, newMessage]);
        setMessage("");
      } catch (error) {
        console.error("Error sending message:", error);
      } finally {
        setIsSending(false);
      }
    },
    [message, currentUserName, room, isSending]
  );

  if (!isOpen) return null;

  return (
    <div className="flex h-full flex-col bg-[#1f1f1f] border-l border-border">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="size-5" />
          <h3 className="font-semibold ">Trò chuyện</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Đóng chat"
          className="h-8 w-8 p-0"
        >
          ×
        </Button>
      </div>

      <ScrollArea className="flex-1 px-4">
        <div className="space-y-4 py-4">
          {messages.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm">
              Chưa có tin nhắn nào
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex flex-col gap-1",
                  msg.isLocal ? "items-end" : "items-start"
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-3 py-2",
                    msg.isLocal
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  )}
                >
                  {!msg.isLocal && (
                    <div className="text-xs font-medium mb-1 opacity-80">
                      {msg.sender}
                    </div>
                  )}
                  <div className="text-sm">{msg.message}</div>
                  <div className="text-xs opacity-60 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString("vi-VN", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <form
        onSubmit={handleSendMessage}
        className="border-t border-border p-4"
      >
        <div className="flex gap-2">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Nhập tin nhắn..."
            className="min-h-[60px] resize-none"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            aria-label="Nhập tin nhắn"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!message.trim() || isSending}
            aria-label="Gửi tin nhắn"
            className="h-[60px] w-[60px] shrink-0"
          >
            <Send className="size-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

